/**
 * aya-slideshow-a11y.js
 *
 * Backfills two WAI-ARIA gaps in Horizon's native <slideshow-component>
 * (assets/slideshow.js, snippets/slideshow.liquid, slideshow-controls.liquid)
 * that Lighthouse/axe flag as accessibility errors on every page that uses a
 * native carousel — home page recommendations, Cross Sell 01/02, product
 * recommendations on product/cart pages, and any future use:
 *
 *   1. `aria-allowed-attr` — the pagination dots in `.slideshow-controls__dots`
 *      get `aria-selected` set by Horizon's own script, but the buttons carry
 *      no ARIA role that permits that attribute. Backfilled with the
 *      standard WAI-ARIA APG tabs pattern: `role="tablist"` on the list,
 *      `role="presentation"` on each `<li>` wrapper (so it doesn't sit
 *      between the tablist and its tabs in the accessibility tree), and
 *      `role="tab"` on each button — which also resolves the matching
 *      `aria-required-children` finding (a tablist whose children aren't
 *      tabs).
 *   2. `aria-hidden-focus` — `<slideshow-slide aria-hidden="true">` still
 *      contains focusable links/buttons (product cards), so a sighted
 *      keyboard user can tab into content a screen reader has been told
 *      isn't there. Focusable descendants get `tabindex="-1"` while their
 *      slide is aria-hidden, and their original tabindex restored the moment
 *      it isn't — so nothing is permanently removed from the tab order, only
 *      while genuinely hidden.
 *
 * This does not modify assets/slideshow.js or any Horizon snippet — it wraps
 * the rendered markup and watches the same `aria-hidden` attribute Horizon's
 * own script already manages (Agents.md §5: "wrap it or listen to its
 * events rather than forking it"). Every change here is an ARIA attribute;
 * nothing changes layout, spacing, color or visible content.
 */
(() => {
  function patchDots(root) {
    root.querySelectorAll('.slideshow-controls__dots:not([role])').forEach((list) => {
      list.setAttribute('role', 'tablist');
      list.querySelectorAll(':scope > li').forEach((li) => {
        li.setAttribute('role', 'presentation');
        const button = li.querySelector('button');
        if (button && !button.hasAttribute('role')) {
          button.setAttribute('role', 'tab');
        }
      });
    });
  }

  function syncSlideFocusability(slide) {
    const hidden = slide.getAttribute('aria-hidden') === 'true';
    slide
      .querySelectorAll('a[href], button, input, select, textarea, [tabindex]')
      .forEach((el) => {
        if (hidden) {
          if (!el.hasAttribute('data-aya-prev-tabindex')) {
            el.setAttribute('data-aya-prev-tabindex', el.getAttribute('tabindex') ?? '');
          }
          el.setAttribute('tabindex', '-1');
        } else if (el.hasAttribute('data-aya-prev-tabindex')) {
          const prev = el.getAttribute('data-aya-prev-tabindex');
          el.removeAttribute('data-aya-prev-tabindex');
          if (prev) {
            el.setAttribute('tabindex', prev);
          } else {
            el.removeAttribute('tabindex');
          }
        }
      });
  }

  function watchSlides(root) {
    root.querySelectorAll('slideshow-slide').forEach((slide) => {
      if (slide.dataset.ayaA11yWatched) return;
      slide.dataset.ayaA11yWatched = 'true';
      syncSlideFocusability(slide);
      new MutationObserver(() => syncSlideFocusability(slide)).observe(slide, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    });
  }

  function init(root) {
    const scope = root || document;
    patchDots(scope);
    watchSlides(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Theme editor: re-run when a section (re)loads, so a slideshow added or
  // reordered in the editor is patched too.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
